/**
 * Audio Engine
 * Handles all game audio using Web Audio API
 * Features: LFO heartbeat, tension curve, procedural horror sounds
 */

import { CONSTANTS } from '../config.js';
import { lerp } from './Utils.js';

export class AudioEngine {
    constructor() {
        this.ctx = null;
        this.masterGain = null;
        this.initialized = false;

        // Heartbeat system
        this.heartbeatRunning = false;
        this.heartbeatTimeoutId = null;
        this.currentHeartbeatRate = 1.0; // Hz (60 BPM)

        // Tension state (0-1)
        this.tensionLevel = 0;
        this.targetTensionLevel = 0;

        // Ambient drone
        this.ambientOsc = null;
        this.ambientGain = null;
        this.ambientLFO = null;
    }

    init() {
        if (this.initialized) return;

        try {
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
            this.masterGain = this.ctx.createGain();
            this.masterGain.connect(this.ctx.destination);
            this.masterGain.gain.value = 0.5;
            this.initialized = true;
        } catch (e) {
            console.warn('Web Audio API not supported:', e);
        }
    }

    // Resume audio context (needed after user interaction)
    resume() {
        if (this.ctx && this.ctx.state === 'suspended') {
            this.ctx.resume();
        }
    }

    /**
     * Play a simple beep/tone
     */
    beep(frequency, duration, type = 'sine', volume = 0.3) {
        if (!this.initialized) this.init();

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = type;
        osc.frequency.value = frequency;
        gain.gain.setValueAtTime(volume, this.ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + duration);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start();
        osc.stop(this.ctx.currentTime + duration);

        return osc;
    }

    /**
     * Start the heartbeat system
     * Uses LFO to create realistic lub-dub pattern
     */
    startHeartbeat() {
        if (!this.initialized) this.init();
        if (this.heartbeatRunning) return;

        this.heartbeatRunning = true;
        this.scheduleHeartbeat();
    }

    scheduleHeartbeat() {
        if (!this.heartbeatRunning) return;

        const now = this.ctx.currentTime;

        // Calculate beat interval from current rate
        // Rate is in Hz (beats per second)
        const beatInterval = 1 / this.currentHeartbeatRate;

        // Create lub-dub pattern
        this.playHeartbeatPulse(now, 'lub');
        this.playHeartbeatPulse(now + 0.15, 'dub');

        // Schedule next heartbeat
        this.heartbeatTimeoutId = setTimeout(() => {
            this.scheduleHeartbeat();
        }, beatInterval * 1000);
    }

    playHeartbeatPulse(time, type) {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        // Different frequencies for lub vs dub
        const baseFreq = type === 'lub' ? 60 : 50;
        const volume = type === 'lub' ? 0.2 : 0.15;
        const duration = type === 'lub' ? 0.12 : 0.1;

        osc.type = 'sine';
        osc.frequency.setValueAtTime(baseFreq, time);

        // Quick attack, exponential decay for thump
        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(volume * (1 + this.tensionLevel * 0.5), time + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, time + duration);

        // Low-pass filter for warmth
        filter.type = 'lowpass';
        filter.frequency.value = 150 + this.tensionLevel * 100;

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain);

        osc.start(time);
        osc.stop(time + duration + 0.05);
    }

    stopHeartbeat() {
        this.heartbeatRunning = false;
        if (this.heartbeatTimeoutId) {
            clearTimeout(this.heartbeatTimeoutId);
            this.heartbeatTimeoutId = null;
        }
    }

    /**
     * Set tension level based on error margin
     * @param {number} errorMargin - 0 to 100
     */
    setTension(errorMargin) {
        this.targetTensionLevel = errorMargin / 100;
    }

    /**
     * Update audio state (call every frame)
     */
    update(delta) {
        // Smooth tension transition
        this.tensionLevel = lerp(this.tensionLevel, this.targetTensionLevel, 0.02);

        // Update heartbeat rate based on tension
        // 1 Hz (60 BPM) at 0% tension, 3 Hz (180 BPM) at 100%
        const minRate = CONSTANTS.HEARTBEAT_BPM_MIN / 60;
        const maxRate = CONSTANTS.HEARTBEAT_BPM_MAX / 60;
        this.currentHeartbeatRate = lerp(minRate, maxRate, this.tensionLevel);
    }

    /**
     * Subtle dread sound for ghost path trigger
     * Quick audio spike without being too jarring
     */
    playSubtleDread() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        // Low frequency sweep
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(100, now);
        osc.frequency.linearRampToValueAtTime(40, now + 0.3);

        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);

        filter.type = 'lowpass';
        filter.frequency.value = 200;

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + 0.4);

        // Secondary high-frequency "sting"
        const osc2 = this.ctx.createOscillator();
        const gain2 = this.ctx.createGain();

        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(800, now);
        osc2.frequency.linearRampToValueAtTime(400, now + 0.15);

        gain2.gain.setValueAtTime(0.1, now);
        gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

        osc2.connect(gain2);
        gain2.connect(this.masterGain);

        osc2.start(now);
        osc2.stop(now + 0.25);
    }

    /**
     * Horror drone for transition
     */
    playHorrorDrone(duration = 3) {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(80, now);
        osc.frequency.exponentialRampToValueAtTime(25, now + duration);

        gain.gain.setValueAtTime(0.15, now);
        gain.gain.linearRampToValueAtTime(0.25, now + duration * 0.3);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + duration);

        // Add dissonant second voice
        const osc2 = this.ctx.createOscillator();
        const gain2 = this.ctx.createGain();

        osc2.type = 'sawtooth';
        osc2.frequency.setValueAtTime(83, now); // Slightly detuned
        osc2.frequency.exponentialRampToValueAtTime(27, now + duration);

        gain2.gain.setValueAtTime(0.1, now);
        gain2.gain.exponentialRampToValueAtTime(0.01, now + duration);

        osc2.connect(gain2);
        gain2.connect(this.masterGain);

        osc2.start(now);
        osc2.stop(now + duration);
    }

    /**
     * Start ambient horror drone (looping)
     */
    startAmbientDrone() {
        if (!this.initialized) this.init();
        if (this.ambientOsc) return;

        // Low drone oscillator
        this.ambientOsc = this.ctx.createOscillator();
        this.ambientGain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        this.ambientOsc.type = 'sawtooth';
        this.ambientOsc.frequency.value = 55;

        // LFO for pulsing
        this.ambientLFO = this.ctx.createOscillator();
        const lfoGain = this.ctx.createGain();

        this.ambientLFO.frequency.value = 0.5; // Slow pulse
        lfoGain.gain.value = 0.03;

        this.ambientLFO.connect(lfoGain);
        lfoGain.connect(this.ambientGain.gain);

        this.ambientGain.gain.value = 0.08;

        filter.type = 'lowpass';
        filter.frequency.value = 100;

        this.ambientOsc.connect(filter);
        filter.connect(this.ambientGain);
        this.ambientGain.connect(this.masterGain);

        this.ambientOsc.start();
        this.ambientLFO.start();
    }

    stopAmbientDrone() {
        if (this.ambientOsc) {
            this.ambientOsc.stop();
            this.ambientOsc = null;
        }
        if (this.ambientLFO) {
            this.ambientLFO.stop();
            this.ambientLFO = null;
        }
        this.ambientGain = null;
    }

    /**
     * Cheerful correct answer sound (for honeypot phase)
     */
    playCorrect() {
        this.beep(523, 0.1, 'sine', 0.25); // C
        setTimeout(() => this.beep(659, 0.1, 'sine', 0.25), 80); // E
        setTimeout(() => this.beep(784, 0.15, 'sine', 0.3), 160); // G
    }

    /**
     * Wrong answer sound (for honeypot phase)
     */
    playWrong() {
        this.beep(200, 0.15, 'square', 0.2);
        setTimeout(() => this.beep(150, 0.2, 'square', 0.15), 100);
    }

    /**
     * Jump sound
     */
    playJump() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.linearRampToValueAtTime(400, now + 0.1);

        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + 0.2);
    }

    /**
     * Collision/damage sound
     */
    playDamage() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        // Impact
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.linearRampToValueAtTime(80, now + 0.15);

        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + 0.25);

        // Noise burst
        const bufferSize = this.ctx.sampleRate * 0.1;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);

        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.2));
        }

        const noise = this.ctx.createBufferSource();
        const noiseGain = this.ctx.createGain();

        noise.buffer = buffer;
        noiseGain.gain.setValueAtTime(0.15, now);
        noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

        noise.connect(noiseGain);
        noiseGain.connect(this.masterGain);

        noise.start(now);
    }

    /**
     * UI click sound
     */
    playClick() {
        this.beep(600, 0.05, 'sine', 0.2);
    }

    /**
     * Victory fanfare
     */
    playVictory() {
        this.beep(523, 0.15, 'sine', 0.3);
        setTimeout(() => this.beep(659, 0.15, 'sine', 0.3), 150);
        setTimeout(() => this.beep(784, 0.15, 'sine', 0.3), 300);
        setTimeout(() => this.beep(1047, 0.3, 'sine', 0.4), 450);
    }

    /**
     * Game over sound
     */
    playGameOver() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        // Descending tone
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.exponentialRampToValueAtTime(30, now + 1.5);

        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 1.5);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + 1.6);
    }

    /**
     * Boss phase transition - dramatic sting
     */
    playBossTransition() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        // Impact hit
        const impact = this.ctx.createOscillator();
        const impactGain = this.ctx.createGain();
        impact.type = 'sawtooth';
        impact.frequency.setValueAtTime(80, now);
        impact.frequency.exponentialRampToValueAtTime(30, now + 0.5);
        impactGain.gain.setValueAtTime(0.4, now);
        impactGain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
        impact.connect(impactGain);
        impactGain.connect(this.masterGain);
        impact.start(now);
        impact.stop(now + 0.7);

        // Rising sting
        const sting = this.ctx.createOscillator();
        const stingGain = this.ctx.createGain();
        sting.type = 'square';
        sting.frequency.setValueAtTime(200, now + 0.1);
        sting.frequency.linearRampToValueAtTime(600, now + 0.4);
        stingGain.gain.setValueAtTime(0.15, now + 0.1);
        stingGain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
        sting.connect(stingGain);
        stingGain.connect(this.masterGain);
        sting.start(now + 0.1);
        sting.stop(now + 0.6);
    }

    /**
     * Overfitter growl/roar - menacing presence
     */
    playOverfitterGrowl() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        // Low rumble
        const osc1 = this.ctx.createOscillator();
        const gain1 = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        osc1.type = 'sawtooth';
        osc1.frequency.setValueAtTime(40, now);
        osc1.frequency.linearRampToValueAtTime(60, now + 0.3);
        osc1.frequency.linearRampToValueAtTime(35, now + 0.8);

        filter.type = 'lowpass';
        filter.frequency.value = 150;
        filter.Q.value = 5;

        gain1.gain.setValueAtTime(0, now);
        gain1.gain.linearRampToValueAtTime(0.3, now + 0.1);
        gain1.gain.linearRampToValueAtTime(0.25, now + 0.5);
        gain1.gain.exponentialRampToValueAtTime(0.01, now + 1.0);

        osc1.connect(filter);
        filter.connect(gain1);
        gain1.connect(this.masterGain);

        osc1.start(now);
        osc1.stop(now + 1.1);

        // Dissonant overtone
        const osc2 = this.ctx.createOscillator();
        const gain2 = this.ctx.createGain();
        osc2.type = 'sawtooth';
        osc2.frequency.setValueAtTime(43, now);
        osc2.frequency.linearRampToValueAtTime(38, now + 0.8);
        gain2.gain.setValueAtTime(0.1, now);
        gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.9);
        osc2.connect(gain2);
        gain2.connect(this.masterGain);
        osc2.start(now);
        osc2.stop(now + 1.0);
    }

    /**
     * Warning alarm - pulsing alert when near death
     */
    playWarningAlarm() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        for (let i = 0; i < 3; i++) {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();

            osc.type = 'square';
            osc.frequency.value = 440;

            const startTime = now + i * 0.2;
            gain.gain.setValueAtTime(0.2, startTime);
            gain.gain.setValueAtTime(0.01, startTime + 0.1);

            osc.connect(gain);
            gain.connect(this.masterGain);

            osc.start(startTime);
            osc.stop(startTime + 0.15);
        }
    }

    /**
     * Data corruption glitch noise
     */
    playGlitchNoise() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        // Create noise buffer
        const bufferSize = this.ctx.sampleRate * 0.15;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);

        // Glitchy pattern
        for (let i = 0; i < bufferSize; i++) {
            if (Math.random() < 0.1) {
                data[i] = (Math.random() > 0.5 ? 1 : -1) * 0.8;
            } else {
                data[i] = (Math.random() * 2 - 1) * 0.3;
            }
        }

        const noise = this.ctx.createBufferSource();
        const noiseGain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        noise.buffer = buffer;
        filter.type = 'bandpass';
        filter.frequency.value = 2000;
        filter.Q.value = 1;

        noiseGain.gain.setValueAtTime(0.2, now);
        noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

        noise.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(this.masterGain);

        noise.start(now);
    }

    /**
     * Eerie whisper - barely audible unsettling sound
     */
    playWhisper() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        // Breathy noise
        const bufferSize = this.ctx.sampleRate * 0.5;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);

        for (let i = 0; i < bufferSize; i++) {
            const envelope = Math.sin(Math.PI * i / bufferSize);
            data[i] = (Math.random() * 2 - 1) * envelope * 0.5;
        }

        const noise = this.ctx.createBufferSource();
        const noiseGain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        noise.buffer = buffer;
        filter.type = 'bandpass';
        filter.frequency.value = 800;
        filter.Q.value = 3;

        noiseGain.gain.setValueAtTime(0.08, now);
        noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

        noise.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(this.masterGain);

        noise.start(now);
    }

    /**
     * Projectile fire sound
     */
    playProjectileFire() {
        if (!this.initialized) this.init();

        const now = this.ctx.currentTime;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(100, now + 0.15);

        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + 0.25);
    }

    /**
     * Set master volume (0-1)
     */
    setVolume(value) {
        if (this.masterGain) {
            this.masterGain.gain.value = Math.max(0, Math.min(1, value));
        }
    }

    /**
     * Clean up all audio resources
     */
    destroy() {
        this.stopHeartbeat();
        this.stopAmbientDrone();

        if (this.ctx) {
            this.ctx.close();
        }
    }
}

// Singleton instance
export const audioEngine = new AudioEngine();
